import type { FreeArea, OrientationType } from "@rubyplay/effw-connector";
import {
	type HostSessionEvent,
	type HostSessionListener,
	type HostSessionSnapshot,
	type HostSessionState,
	createInitialHostSessionState,
} from "@/host/HostSessionTypes";

function copyFreeAreas(freeAreas: readonly FreeArea[]): FreeArea[] {
	return freeAreas.map((area) => ({ ...area }));
}

function buildFrozenSnapshot(state: HostSessionState): HostSessionSnapshot {
	const frozenConfig = state.config
		? Object.freeze({
				...state.config,
			})
		: null;

	return Object.freeze({
		phase: state.phase,
		transportReady: state.transportReady,
		configured: state.configured,
		startupPreloadRequested: state.startupPreloadRequested,
		gameReady: state.gameReady,
		config: frozenConfig,
		configuredFreeAreas: copyFreeAreas(state.configuredFreeAreas),
		freeAreas: copyFreeAreas(state.freeAreas),
		lastUpdatedAtMs: state.lastUpdatedAtMs,
	});
}

function nowMs(): number {
	return Date.now();
}

export class HostSessionStore {
	private state: HostSessionState = createInitialHostSessionState();
	private snapshot: HostSessionSnapshot = buildFrozenSnapshot(this.state);
	private readonly listeners = new Set<HostSessionListener>();

	public getSnapshot(): HostSessionSnapshot {
		return this.snapshot;
	}

	public subscribe(listener: HostSessionListener): () => void {
		this.listeners.add(listener);

		return () => {
			this.listeners.delete(listener);
		};
	}

	public dispatch(event: HostSessionEvent): void {
		const prevSnapshot = this.snapshot;
		const nextState = this.reduce(this.state, event);

		if (nextState === this.state) {
			return;
		}

		this.state = nextState;
		this.snapshot = buildFrozenSnapshot(nextState);

		const nextSnapshot = this.snapshot;

		for (const listener of this.listeners) {
			listener(nextSnapshot, prevSnapshot);
		}
	}

	public reset(reason: string): void {
		this.dispatch({ type: "RESET", reason });
	}

	private reduce(
		currentState: HostSessionState,
		event: HostSessionEvent
	): HostSessionState {
		switch (event.type) {
			case "TRANSPORT_READY": {
				if (currentState.transportReady) {
					return currentState;
				}

				return {
					...currentState,
					phase: "transport_ready",
					transportReady: true,
					lastUpdatedAtMs: nowMs(),
				};
			}

			case "CONFIG_RECEIVED": {
				return {
					...currentState,
					phase: currentState.gameReady ? "game_ready" : "configured",
					transportReady: true,
					configured: true,
					config: {
						...event.payload.config,
					},
					configuredFreeAreas: copyFreeAreas(event.payload.freeAreas),
					freeAreas: copyFreeAreas(event.payload.freeAreas),
					lastUpdatedAtMs: nowMs(),
				};
			}

			case "CONFIGURED_FREE_AREAS_UPDATED": {
				if (!currentState.configured) {
					return currentState;
				}

				return {
					...currentState,
					configuredFreeAreas: copyFreeAreas(event.payload.freeAreas),
					lastUpdatedAtMs: nowMs(),
				};
			}

			case "FREE_AREAS_UPDATED": {
				if (!currentState.configured) {
					return currentState;
				}

				return {
					...currentState,
					freeAreas: copyFreeAreas(event.payload.freeAreas),
					lastUpdatedAtMs: nowMs(),
				};
			}

			case "ORIENTATION_UPDATED": {
				if (!currentState.config) {
					return currentState;
				}

				const nextOrientation: OrientationType = event.payload.orientation;

				if (currentState.config.orientation === nextOrientation) {
					return currentState;
				}

				return {
					...currentState,
					config: {
						...currentState.config,
						orientation: nextOrientation,
					},
					lastUpdatedAtMs: nowMs(),
				};
			}

			case "STARTUP_PRELOAD_RECEIVED": {
				if (currentState.startupPreloadRequested) {
					return currentState;
				}

				return {
					...currentState,
					transportReady: true,
					startupPreloadRequested: true,
					lastUpdatedAtMs: nowMs(),
				};
			}

			case "GAME_READY_RECEIVED": {
				if (currentState.gameReady) {
					return currentState;
				}

				return {
					...currentState,
					phase: "game_ready",
					transportReady: true,
					gameReady: true,
					lastUpdatedAtMs: nowMs(),
				};
			}

			case "RESET": {
				return createInitialHostSessionState();
			}
		}
	}
}

let singletonHostSessionStore: HostSessionStore | null = null;

export function getHostSessionStore(): HostSessionStore {
	if (!singletonHostSessionStore) {
		singletonHostSessionStore = new HostSessionStore();
	}

	return singletonHostSessionStore;
}
